import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  Transfer,
  Checkbox,
  InputNumber,
  SearchPaginatedSelect,
  Select,
  Table,
  AntTable,
} from "@/frontend/components/common";
import { Space, DatePicker } from "antd";
import dayjs from "dayjs";
import {
  tournamentService,
  disciplineService,
  tournamentStatusService,
  tournamentFormatService,
} from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import { Form as AntForm } from "antd";
import type {
  CreateTournamentRequestDTO,
  UpdateTournamentRequestDTO,
  TournamentResponseDTO,
  DisciplineResponseDTO,
  TournamentStatusResponseDTO,
  CountryResponseDTO,
  ApiResponse,
} from "@/shared/dto";
import { useEffect, useState } from "react";

interface SeasonFormValue {
  id?: string;
  season: string;
  start?: string;
  end?: string;
  statusId?: string;
  formatId?: string;
  isCurrent?: boolean;
}

interface TournamentFormValues {
  name: string;
  description?: string;
  discipline?: DisciplineResponseDTO;
  type?: string;
  sortOrder?: number;
  country?: { id: string; name: string };
  configuration: {
    hasReferees: boolean;
    hasLineups: boolean;
    hasStadium: boolean;
    hasTeams: boolean;
    hasSubstitutions: boolean;
    hasCards: boolean;
    maxPlayersPerTeam?: number;
    minPlayersPerTeam?: number;
    eventTypes: string;
  };
  seasons?: SeasonFormValue[];
  newSeasons?: SeasonFormValue[];
  statusIds?: string[];
  statusId?: string;
  status?: string;
}

interface TournamentFormProps {
  tournamentId?: string;
  initialValues?: Partial<TournamentFormValues>;
}

export function TournamentForm({
  tournamentId,
  initialValues,
}: TournamentFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!tournamentId;

  const [statuses, setStatuses] = useState<TournamentStatusResponseDTO[]>([]);
  const [formats, setFormats] = useState<any[]>([]);
  const [formatsLoading, setFormatsLoading] = useState(false);

  const seasonValidation = (
    existing: SeasonFormValue[],
    newSeas: SeasonFormValue[],
  ) => {
    const all = [...existing, ...newSeas];
    const currentCount = all.filter((s) => s.isCurrent).length;
    if (currentCount > 1)
      return "Sólo una temporada puede estar marcada como actual";

    const activeStatus = statuses.find(
      (s) => s.slug === "in-progress" || s.slug === "active",
    );
    if (activeStatus) {
      const cnt = all.filter((s) => s.statusId === activeStatus.id).length;
      if (cnt > 1) return "Sólo una temporada activa está permitida";
    }

    for (let i = 0; i < all.length; i++) {
      const a = all[i];
      if (!a) continue;
      if (!a.formatId) {
        return "Cada temporada debe tener un formato seleccionado";
      }
      const aStart = a.start ? new Date(a.start) : null;
      const aEnd = a.end ? new Date(a.end) : null;
      if (
        aEnd &&
        aEnd < new Date() &&
        (statuses.find((s) => s.id === a.statusId)?.isActive || a.isCurrent)
      ) {
        if (a.isCurrent) {
          const hasNewer = all.some((b, j) => {
            if (j === i) return false;
            const bStart = b.start ? new Date(b.start) : null;
            return bStart && aEnd && bStart > aEnd;
          });
          if (!hasNewer) {
          } else {
            return "Una temporada activa o actual no puede terminar en el pasado";
          }
        } else {
          return "Una temporada activa o actual no puede terminar en el pasado";
        }
      }
      if (aStart && aEnd && aStart > aEnd) {
        return "Rango de fechas inválido en una temporada";
      }
      for (let j = i + 1; j < all.length; j++) {
        const b = all[j];
        if (!b) continue;
        const bStart = b.start ? new Date(b.start) : null;
        const bEnd = b.end ? new Date(b.end) : null;
        if (aStart && aEnd && bStart && bEnd) {
          if (aStart <= bEnd && bStart <= aEnd) {
            return "Las fechas de las temporadas se solapan";
          }
        }
      }
    }
    return null;
  };
  const [transferKeys, setTransferKeys] = useState<string[]>(
    () => (initialValues?.statusIds as string[]) || [],
  );
  const [disciplineQuery, setDisciplineQuery] = useState<string>(
    initialValues?.discipline?.name || "",
  );
  // Ocultar secciones avanzadas (estados, configuración, temporadas) pero conservar el código
  const showAdvanced = false;

  useEffect(() => {
    (async () => {
      const res = await tournamentStatusService.getPaginate({
        page: 1,
        pageSize: 100,
      });
      if (res?.success && res.data) {
        setStatuses(res.data.data || []);
      }
      try {
        const fmt = await tournamentFormatService.getPaginate({
          page: 1,
          pageSize: 100,
        });
        if (fmt?.success && fmt.data) {
          setFormats(fmt.data.data || []);
        }
      } catch (e) {
        console.error("error loading formats", e);
      }
    })();

    if (initialValues?.discipline) {
      setDisciplineQuery(initialValues.discipline.name);
      form.setFieldsValue({
        discipline: initialValues.discipline,
      });
    }
  }, []);

  const [existingSeasons, setExistingSeasons] = useState<SeasonFormValue[]>(
    initialValues?.seasons || [],
  );

  const availableStatuses = statuses.filter((s) => transferKeys.includes(s.id));

  useEffect(() => {
    if (initialValues && isEditMode) {
      const vals: any = { ...initialValues };
      delete vals.seasons;
      form.setFieldsValue(vals);
      setTransferKeys((initialValues.statusIds as string[]) || []);
      setExistingSeasons(initialValues.seasons || []);

      if (initialValues.discipline) {
        setDisciplineQuery(initialValues.discipline.name);
      }
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: TournamentFormValues) => {
    const makeExistingPayload = (s: SeasonFormValue) => ({
      id: s.id!,
      season: s.season,
      start: s.start ? new Date(s.start) : undefined,
      end: s.end ? new Date(s.end) : undefined,
      statusId: s.statusId,
      formatId: s.formatId!,
      isCurrent: s.isCurrent,
    });

    const makeNewPayload = (s: SeasonFormValue) => ({
      season: s.season,
      start: s.start ? new Date(s.start) : undefined,
      end: s.end ? new Date(s.end) : undefined,
      statusId: s.statusId,
      formatId: s.formatId!,
      isCurrent: s.isCurrent,
    });
    try {
      const finalStatusIds =
        transferKeys && transferKeys.length > 0
          ? transferKeys
          : values.statusIds || [];
      const finalStatusId =
        values.statusId && finalStatusIds.includes(values.statusId)
          ? values.statusId
          : undefined;

      let response: ApiResponse<TournamentResponseDTO>;

      // Construir una única temporada por defecto basada en el rango de fechas del formulario
      const startVal: any = (values as any).tournamentStart;
      const endVal: any = (values as any).tournamentEnd;
      const startDate = startVal ? new Date(startVal) : undefined;
      const endDate = endVal ? new Date(endVal) : undefined;

      const defaultFormatId =
        formats && formats.length > 0 ? formats[0].id : "";
      const seasonName =
        startDate && endDate ? `${startDate.getFullYear()}` : "Default";

      const seasonsPayload = [
        {
          season: seasonName,
          start: startDate,
          end: endDate,
          statusId: undefined,
          formatId: defaultFormatId,
          isCurrent: true,
        },
      ];

      // Forzamos valores predeterminados mínimos para compatibilidad
      const config = values.configuration || {
        hasReferees: false,
        hasLineups: false,
        hasStadium: false,
        hasTeams: true,
        hasSubstitutions: false,
        hasCards: false,
        maxPlayersPerTeam: undefined,
        minPlayersPerTeam: undefined,
        eventTypes: "",
      };

      const payloadCreate:
        | CreateTournamentRequestDTO
        | UpdateTournamentRequestDTO = {
        name: values.name,
        description: values.description,
        type: values.discipline?.name || values.type || "league",
        disciplineId: values.discipline?.id,
        sortOrder:
          values.sortOrder !== undefined && values.sortOrder !== null
            ? parseFloat(String(values.sortOrder))
            : undefined,
        countryId: values.country?.id,
        seasons: seasonsPayload,
        configuration: {
          hasReferees: config.hasReferees,
          hasLineups: config.hasLineups,
          hasStadium: config.hasStadium,
          hasTeams: config.hasTeams,
          hasSubstitutions: config.hasSubstitutions,
          hasCards: config.hasCards,
          maxPlayersPerTeam: config.maxPlayersPerTeam,
          minPlayersPerTeam: config.minPlayersPerTeam,
          eventTypes: config.eventTypes
            ? config.eventTypes
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        },
        statusIds: finalStatusIds,
        statusId: finalStatusId,
        canDelete: true,
        canEdit: true,
        isSystem: false,
      };

      if (showAdvanced) {
        const seasonError = seasonValidation(
          existingSeasons,
          values.newSeasons || [],
        );
        if (seasonError) {
          Message.error({ content: seasonError });
          return;
        }
      }

      if (isEditMode && tournamentId) {
        response = await tournamentService.update(
          tournamentId,
          payloadCreate as UpdateTournamentRequestDTO,
        );
      } else {
        response = await tournamentService.create(
          payloadCreate as CreateTournamentRequestDTO,
        );
      }

      if (response.success && response.data) {
        Message.success({
          content: isEditMode
            ? "Torneo actualizado correctamente"
            : "Torneo creado correctamente",
        });
        navigate("/tournaments");
      } else {
        Message.error({
          content:
            response.error ||
            (isEditMode
              ? "Error al actualizar torneo"
              : "Error al crear torneo"),
        });
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : isEditMode
            ? "Error al actualizar torneo"
            : "Error al crear torneo";
      Message.error({ content: errorMessage });
    }
  };

  // La UI se simplifica: no hay pasos, mostramos todo en una sola vista General

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
      initialValues={{
        configuration: {
          hasReferees: false,
          hasLineups: false,
          hasStadium: false,
          hasTeams: true,
          hasSubstitutions: false,
          hasCards: false,
          eventTypes: "",
        },
      }}
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Información General</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Vista General simplificada */}
        <div>
          <FormItem
            name="name"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es requerido" }]}
          >
            <Input placeholder="Nombre del torneo" size="large" />
          </FormItem>

          <FormItem
            name="description"
            label="Descripción"
            rules={[{ required: true, message: "La descripción es requerida" }]}
          >
            <TextArea
              placeholder="Descripción del torneo (opcional)"
              rows={3}
              size="large"
            />
          </FormItem>

          <FormItem
            name="discipline"
            label="Tipo de torneo"
            rules={[{ required: true, message: "El tipo es requerido" }]}
          >
            <SearchPaginatedSelect<DisciplineResponseDTO>
              placeholder="Buscar tipo de torneo..."
              fetchFn={disciplineService.getPaginate}
              filterField="name"
              initialQuery={disciplineQuery}
              onSelect={(discipline) => {
                setDisciplineQuery(discipline.name);
                form.setFieldsValue({ discipline });
              }}
              renderItem={(d) => (
                <div className="flex items-center gap-2">
                  <span>{d.name}</span>
                </div>
              )}
              getItemKey={(d) => d.id}
              minLength={1}
              debounceMs={300}
              allowButtonEmptyQuery={true}
              errorMessage="Error al buscar tipos de torneo"
            />
          </FormItem>

          <FormItem name={["tournamentRange"]} label="Fechas del torneo">
            <DatePicker.RangePicker
              className="w-full"
              onChange={(dates) => {
                const [s, e] = dates || [];
                form.setFieldsValue({
                  tournamentStart: s ? s.format("YYYY-MM-DD") : undefined,
                  tournamentEnd: e ? e.format("YYYY-MM-DD") : undefined,
                });
              }}
            />
          </FormItem>
        </div>

        {/* Campos ocultos para almacenar las fechas */}
        <FormItem name="tournamentStart" hidden>
          <Input />
        </FormItem>
        <FormItem name="tournamentEnd" hidden>
          <Input />
        </FormItem>
        {/* Secciones avanzadas están ocultas (showAdvanced = false) */}
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Space>
          <Button type="primary" htmlType="submit" size="large">
            {isEditMode ? "Actualizar Torneo" : "Crear Torneo"}
          </Button>

          <Button size="large" onClick={() => navigate("/tournaments")}>
            Cancelar
          </Button>
        </Space>
      </div>
    </Form>
  );
}
